import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import Hls from "hls.js";
import { Message, Conversation } from "../types";
import {
  MessageAreaContainer,
  MessagesList,
  MessageBubble,
  Avatar,
  Badge,
  Backdrop,
  ModalContainer,
  CloseButton,
  TypingIndicator,
  SystemMessage,
  UnreadSeparator,
} from "../styles";
import { format, isToday, isYesterday } from "date-fns";
import OrderInfo from "./OrderInfo";

import {
  Headphones,
  Play,
  FileText,
  Download,
  X,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Phone,
  Video,
  PhoneOff,
  RotateCcw,
} from "lucide-react";
import { useCall } from "@context/CallContext";
import { Helper } from "@utils/index";

const VideoPlayer: React.FC<{
  url: string;
  orientation?: "portrait" | "landscape";
  aspectRatio?: number;
}> = ({ url, orientation, aspectRatio }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls: Hls | null = null;

    if (url.endsWith(".m3u8")) {
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // Native support (Safari)
        video.src = url;
      } else if (Hls.isSupported()) {
        // Use Hls.js
        hls = new Hls();
        hls.loadSource(url);
        hls.attachMedia(video);
      }
    } else {
      video.src = url;
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [url]);

  return (
    <video
      ref={videoRef}
      controls
      autoPlay
      muted
      playsInline
      style={{
        maxWidth: "100%",
        maxHeight: orientation === "portrait" ? "80vh" : "100%",
        borderRadius: "12px",
        objectFit: "contain",
        background: "#000",
        display: "block",
        margin: "0 auto",
      }}
    />
  );
};

interface Props {
  messages: Message[];
  activeConversation: Conversation | null;
  currentUserId: number;
  currentUserRole: string;
  isTyping: boolean;
  unreadCount?: number;
  onStartSupport: () => Promise<any>;
  onLoadMore: () => Promise<any>;
  hasMore: boolean;
  isLoadingMore: boolean;
}

const MessageArea: React.FC<Props> = ({
  messages,
  activeConversation,
  currentUserId,
  currentUserRole,
  isTyping,
  unreadCount = 0,
  onStartSupport,
  onLoadMore,
  hasMore,
  isLoadingMore,
}) => {
  const {
    callState,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleAudio,
    toggleVideo,
  } = useCall();
  const endRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lastScrollHeight = useRef<number>(0);
  const prevConvId = useRef<number | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [activeMedia, setActiveMedia] = useState<{
    url: string;
    type: string;
    metadata?: any;
  } | null>(null);

  const allMediaFiles = React.useMemo(() => {
    const mediaFiles: {
      url: string;
      type: string;
      thumbnail?: string;
      metadata?: any;
    }[] = [];
    messages.forEach((msg) => {
      if (msg.files) {
        msg.files.forEach((file) => {
          if (
            file.fileType?.startsWith("image/") ||
            file.fileType?.startsWith("video/")
          ) {
            mediaFiles.push({
              url: file.url,
              type: file.fileType.startsWith("image/") ? "image" : "video",
              thumbnail: file.fileType.startsWith("video/")
                ? file.metadata?.videoImage || "/video-placeholder.png"
                : file.url,
              metadata: file.metadata,
            });
          }
        });
      }
    });
    return mediaFiles;
  }, [messages]);

  const activeMediaIndex = activeMedia
    ? allMediaFiles.findIndex((m) => m.url === activeMedia.url)
    : -1;

  const handlePrevMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeMediaIndex > 0) {
      setActiveMedia({
        url: allMediaFiles[activeMediaIndex - 1].url,
        type: allMediaFiles[activeMediaIndex - 1].type,
        metadata: allMediaFiles[activeMediaIndex - 1].metadata,
      });
    } else {
      setActiveMedia({
        url: allMediaFiles[allMediaFiles.length - 1].url,
        type: allMediaFiles[allMediaFiles.length - 1].type,
        metadata: allMediaFiles[allMediaFiles.length - 1].metadata,
      });
    }
  };

  const handleNextMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeMediaIndex < allMediaFiles.length - 1) {
      setActiveMedia({
        url: allMediaFiles[activeMediaIndex + 1].url,
        type: allMediaFiles[activeMediaIndex + 1].type,
        metadata: allMediaFiles[activeMediaIndex + 1].metadata,
      });
    } else {
      setActiveMedia({
        url: allMediaFiles[0].url,
        type: allMediaFiles[0].type,
        metadata: allMediaFiles[0].metadata,
      });
    }
  };

  useEffect(() => {
    if (!isLoadingMore && listRef.current && endRef.current) {
      const parent = listRef.current;
      const isNewConversation = activeConversation?.id !== prevConvId.current;

      if (isNewConversation) {
        parent.scrollTop = parent.scrollHeight;
        prevConvId.current = activeConversation?.id || null;
      } else {
        endRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages, isTyping, isLoadingMore, activeConversation?.id]);

  useEffect(() => {
    if (isLoadingMore) {
      if (listRef.current) {
        lastScrollHeight.current = listRef.current.scrollHeight;
      }
    } else if (lastScrollHeight.current > 0 && listRef.current) {
      const delta = listRef.current.scrollHeight - lastScrollHeight.current;
      listRef.current.scrollTop += delta;
      lastScrollHeight.current = 0;
    }
  }, [messages, isLoadingMore]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollTop === 0 && hasMore && !isLoadingMore) {
      onLoadMore();
    }
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 150;
    setShowScrollButton(!isNearBottom);
  };

  if (!activeConversation) {
    return (
      <MessageAreaContainer
        style={{
          justifyContent: "center",
          alignItems: "center",
          color: "#999",
          gap: "20px",
          flexDirection: "column",
        }}
      >
        <p>Select a conversation to start chatting</p>
        {currentUserRole !== "admin" && (
          <button
            onClick={onStartSupport}
            style={{
              padding: "12px 24px",
              background: "#ff3135",
              color: "white",
              border: "none",
              borderRadius: "30px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: 600,
              boxShadow: "0 4px 15px rgba(255, 49, 53, 0.3)",
              fontSize: "14px",
            }}
          >
            <Headphones size={18} />
            Chat with Admin
          </button>
        )}
      </MessageAreaContainer>
    );
  }

  return (
    <MessageAreaContainer style={{ position: "relative" }}>
      <div
        style={{
          padding: "15px 25px",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          background: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: 600,
              margin: 0,
              marginBottom: "4px",
            }}
          >
            {activeConversation.type === "order"
              ? `Order #${activeConversation.orderId}`
              : activeConversation.subject || "Direct Chat"}
          </h3>
          <p style={{ fontSize: "12px", color: "#666", margin: 0 }}>
            {activeConversation.participants.length} Participants
          </p>
        </div>

        {activeConversation.type === "order" && activeConversation.orderId && (
          <OrderInfo order={activeConversation.order || { id: activeConversation.orderId } as any} />
        )}

        <div style={{ display: "flex", gap: "15px", marginLeft: "20px" }}>
          <button
            onClick={() => startCall(activeConversation.id, "audio")}
            style={{
              background: "none",
              border: "none",
              padding: "8px",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseOut={(e) => (e.currentTarget.style.background = "none")}
          >
            <Phone size={20} color="#666" />
          </button>
          <button
            onClick={() => startCall(activeConversation.id, "video")}
            style={{
              background: "none",
              border: "none",
              padding: "8px",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseOut={(e) => (e.currentTarget.style.background = "none")}
          >
            <Video size={20} color="#666" />
          </button>
        </div>
      </div>

      <MessagesList ref={listRef} onScroll={handleScroll}>
        {isLoadingMore && (
          <div
            style={{
              textAlign: "center",
              padding: "10px",
              fontSize: "12px",
              color: "#999",
            }}
          >
            Loading older messages...
          </div>
        )}

        {messages.map((msg, i) => {
          const showUnreadSeparator =
            unreadCount > 0 && i === messages.length - unreadCount;
          const isSelf =
            Number(msg.senderId) === Number(currentUserId) &&
            msg.senderRole === currentUserRole;

          const msgDate = new Date(msg.createdAt);
          const prevMsg = messages[i - 1];
          const prevDate = prevMsg ? new Date(prevMsg.createdAt) : null;
          const showDateSeparator =
            !prevDate || msgDate.toDateString() !== prevDate.toDateString();

          let dateLabel = format(msgDate, "MMMM d, yyyy");
          if (isToday(msgDate)) dateLabel = "Today";
          else if (isYesterday(msgDate)) dateLabel = "Yesterday";

          return (
            <React.Fragment key={`msg-container-${msg.id || i}`}>
              {showDateSeparator && (
                <div
                  style={{
                    textAlign: "center",
                    margin: "20px 0",
                    position: "relative",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: 0,
                      right: 0, 
                      height: "1px",
                      background: "rgba(0,0,0,0.05)",
                      zIndex: 0,
                    }}
                  />
                  <span
                    style={{
                      position: "relative",
                      background: "#f5f5f5",
                      padding: "0 10px",
                      fontSize: "11px",
                      color: "rgba(0,0,0,0.4)",
                      fontWeight: 500,
                    }}
                  >
                    {dateLabel}
                  </span>
                </div>
              )}

              {showUnreadSeparator && (
                <UnreadSeparator>
                  <span>
                    {unreadCount}{" "}
                    {unreadCount === 1 ? "New Message" : "New Messages"}
                  </span>
                </UnreadSeparator>
              )}

              {msg.type === "system" ? (
                <SystemMessage
                  isCall={
                    !!(
                      msg.metadata?.callType ||
                      msg.metadata?.type ||
                      msg.metadata?.status === "missed" ||
                      msg.metadata?.status === "ended" ||
                      msg.metadata?.status === "started" ||
                      msg.metadata?.status === "no_answer"
                    )
                  }
                  isSelf={isSelf}
                  className={
                    msg.metadata?.status === "ended"
                      ? "call-ended"
                      : msg.metadata?.status === "started"
                        ? "call-started"
                        : ""
                  }
                >
                  {msg.metadata?.status === "missed" ||
                  msg.metadata?.status === "no_answer" ? (
                    <PhoneOff size={14} color="#ff4d4f" />
                  ) : msg.metadata?.callType === "video" ? (
                    <Video
                      size={14}
                      color={
                        msg.metadata?.status === "ended" ? "#52c41a" : "#1890ff"
                      }
                    />
                  ) : (
                    <Phone
                      size={14}
                      color={
                        msg.metadata?.status === "ended" ? "#52c41a" : "#1890ff"
                      }
                    />
                  )}
                  <span>
                    {msg.content}
                    {msg.metadata?.duration && (
                      <span
                        className={`call-duration ${msg.metadata?.status === "ended" ? "ended" : ""}`}
                      >
                        {msg.metadata.duration}
                      </span>
                    )}
                    <span className="time">
                      {format(new Date(msg.createdAt), "hh:mm aa")}
                    </span>
                  </span>
                </SystemMessage>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isSelf ? "flex-end" : "flex-start",
                    width: "100%",
                  }}
                >
                  {(() => {
                    const showSender =
                      i === 0 ||
                      messages[i - 1].senderId !== msg.senderId ||
                      messages[i - 1].senderRole !== msg.senderRole;
                    const sender = activeConversation.participants.find(
                      (p) =>
                        Number(p.participantId) === Number(msg.senderId) &&
                        p.role === msg.senderRole,
                    );

                    return (
                      showSender &&
                      !isSelf && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#888",
                            marginBottom: "4px",
                            marginLeft: "4px",
                          }}
                        >
                          {sender?.name} • {sender?.role}
                        </div>
                      )
                    );
                  })()}

                  <MessageBubble
                    isSelf={isSelf}
                    isMediaOnly={
                      !msg.content &&
                      msg.files?.length === 1 &&
                      (msg.files[0].fileType?.startsWith("image/") ||
                        msg.files[0].fileType?.startsWith("video/"))
                    }
                    style={{ opacity: msg.status === "sending" ? 0.6 : 1 }}
                  >
                    {msg.files && msg.files.length > 0 && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            msg.files.length > 1
                              ? "repeat(auto-fill, minmax(130px, 1fr))"
                              : "1fr",
                          gap: "4px",
                          marginBottom: msg.content ? "8px" : "0",
                          maxWidth: msg.files.length > 1 ? "380px" : "300px",
                        }}
                      >
                        {msg.files.map((file, idx) => (
                          <div
                            key={idx}
                            style={{
                              borderRadius: "8px",
                              overflow: "hidden",
                              aspectRatio:
                                msg.files!.length > 1
                                  ? "1 / 1"
                                  : file.metadata?.orientation === "portrait" ||
                                      (file.isLocal &&
                                        file.fileType?.startsWith("video/"))
                                    ? "9 / 16"
                                    : file.metadata?.orientation === "landscape"
                                      ? "16 / 9"
                                      : "auto",
                              background: "rgba(0,0,0,0.05)",
                              maxWidth:
                                file.metadata?.orientation === "portrait" ||
                                (file.isLocal &&
                                  file.fileType?.startsWith("video/"))
                                  ? "250px"
                                  : "350px",
                              position: "relative",
                            }}
                          >
                            {file.fileType?.startsWith("image/") ? (
                              <img
                                src={file.url}
                                alt={file.fileName}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit:
                                    msg.files!.length > 1 ? "cover" : "contain",
                                  display: "block",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  setActiveMedia({
                                    url: file.url,
                                    type: "image",
                                    metadata: file.metadata,
                                  })
                                }
                              />
                            ) : file.fileType?.startsWith("video/") ? (
                              <div
                                style={{
                                  position: "relative",
                                  cursor: "pointer",
                                  height: "100%",
                                  background: "#000",
                                }}
                                onClick={() =>
                                  setActiveMedia({
                                    url: file.url,
                                    type: "video",
                                    metadata: file.metadata,
                                  })
                                }
                              >
                                {file.isLocal ? (
                                  <video
                                    src={file.url + "#t=0.1"}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                    }}
                                    preload="metadata"
                                  />
                                ) : (
                                  <img
                                    src={
                                      file.metadata?.videoImage
                                        ? `${file.metadata.videoImage}`
                                        : "/video-placeholder.png"
                                    }
                                    alt="Video"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                    }}
                                  />
                                )}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    background:
                                      msg.status === "sending"
                                        ? "rgba(0,0,0,0.4)"
                                        : "rgba(255,49,53,0.9)",
                                    borderRadius: "50%",
                                    padding: "10px",
                                    color: "white",
                                    display: "flex",
                                  }}
                                >
                                  {msg.status === "sending" ? (
                                    <motion.div
                                      animate={{ rotate: 360 }}
                                      transition={{
                                        duration: 1,
                                        repeat: Infinity,
                                        ease: "linear",
                                      }}
                                    >
                                      <Play size={20} fill="white" />
                                    </motion.div>
                                  ) : (
                                    <Play size={20} fill="white" />
                                  )}
                                </div>
                              </div>
                            ) : (
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  textDecoration: "none",
                                  color: "inherit",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  padding: "10px",
                                  background: "rgba(0,0,0,0.05)",
                                  borderRadius: "8px",
                                  height: "100%",
                                }}
                              >
                                <FileText size={20} />
                                <span
                                  style={{
                                    fontSize: "12px",
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {file.fileName}
                                </span>
                                <Download size={16} />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {msg.content && (
                      <div style={{ wordBreak: "break-word" }}>
                        {msg.content}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "9px",
                        opacity: 0.7,
                        marginTop: "4px",
                        textAlign: "right",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {format(new Date(msg.createdAt), "hh:mm aa")}
                      {msg.status === "sending" && (
                        <span style={{ fontSize: "8px" }}>• Sending...</span>
                      )}
                      {msg.status === "error" && (
                        <span style={{ fontSize: "8px", color: "#ff4d4f" }}>
                          • Failed
                        </span>
                      )}
                      {isSelf && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginLeft: "2px",
                          }}
                        >
                          {(() => {
                            if (
                              msg.status === "sending" ||
                              msg.status === "error"
                            )
                              return null;

                            const otherParticipants =
                              activeConversation?.participants?.filter(
                                (p: any) =>
                                  Number(p.participantId) !==
                                    Number(currentUserId) ||
                                  p.role !== currentUserRole,
                              ) || [];

                            const maxReadId = Math.max(
                              0,
                              ...otherParticipants.map(
                                (p: any) => p.lastReadMessageId || 0,
                              ),
                            );
                            const maxDeliveredId = Math.max(
                              0,
                              ...otherParticipants.map(
                                (p: any) => p.lastDeliveredMessageId || 0,
                              ),
                            );

                            const displayStatus =
                              msg.status === "read" || msg.id <= maxReadId
                                ? "read"
                                : msg.status === "delivered" ||
                                    msg.id <= maxDeliveredId
                                  ? "delivered"
                                  : msg.status === "sent"
                                    ? "sent"
                                    : "sent";

                            if (displayStatus === "sent")
                              return <Check size={12} />;
                            if (displayStatus === "delivered")
                              return (
                                <CheckCheck
                                  size={12}
                                  style={{ color: "#ccc" }}
                                />
                              );
                            if (displayStatus === "read")
                              return (
                                <CheckCheck
                                  size={12}
                                  style={{ color: "#4fc3f7" }}
                                />
                              );
                            return null;
                          })()}
                        </span>
                      )}
                    </div>
                  </MessageBubble>
                </div>
              )}
            </React.Fragment>
          );
        })}

        {isTyping && (
          <TypingIndicator>
            <span></span>
            <span></span>
            <span></span>
          </TypingIndicator>
        )}
        <div ref={endRef} />
      </MessagesList>

      <AnimatePresence>
        {showScrollButton && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={() => {
              endRef.current?.scrollIntoView({ behavior: "smooth" });
              setShowScrollButton(false);
            }}
            style={{
              position: "absolute",
              bottom: "30px",
              right: "30px",
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              background: "white",
              color: "#ff3135",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0,0,0,0.05)",
              zIndex: 10,
            }}
          >
            <ChevronDown size={24} />
          </motion.div>
        )}
      </AnimatePresence>

      {createPortal(
        <AnimatePresence>
          {activeMedia && (
            <Backdrop
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveMedia(null)}
              style={{ zIndex: 999999 }}
            >
              <CloseButton onClick={() => setActiveMedia(null)}>
                <X size={24} />
              </CloseButton>

              {allMediaFiles.length > 1 && (
                <>
                  <div
                    onClick={handlePrevMedia}
                    style={{
                      position: "absolute",
                      left: "20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "rgba(255,255,255,0.2)",
                      padding: "15px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      zIndex: 10002,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(4px)",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(255,49,53,0.8)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        "rgba(255,255,255,0.2)")
                    }
                  >
                    <ChevronLeft size={30} />
                  </div>
                  <div
                    onClick={handleNextMedia}
                    style={{
                      position: "absolute",
                      right: "20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "rgba(255,255,255,0.2)",
                      padding: "15px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      zIndex: 10002,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(4px)",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(255,49,53,0.8)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        "rgba(255,255,255,0.2)")
                    }
                  >
                    <ChevronRight size={30} />
                  </div>
                </>
              )}

              <ModalContainer
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                {activeMedia.type === "image" ? (
                  <img src={activeMedia.url} alt="Full view" />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#000",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <VideoPlayer
                      url={activeMedia.url}
                      orientation={activeMedia.metadata?.orientation}
                      aspectRatio={activeMedia.metadata?.aspectRatio}
                    />
                  </div>
                )}
              </ModalContainer>

              {allMediaFiles.length > 1 && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "30px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: "10px",
                    background: "rgba(0,0,0,0.6)",
                    padding: "10px",
                    borderRadius: "12px",
                    maxWidth: "90vw",
                    overflowX: "auto",
                    zIndex: 10001,
                    backdropFilter: "blur(10px)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {allMediaFiles.map((media, idx) => (
                    <div
                      key={idx}
                      onClick={() =>
                        setActiveMedia({
                          url: media.url,
                          type: media.type,
                          metadata: media.metadata,
                        })
                      }
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        cursor: "pointer",
                        border:
                          activeMedia.url === media.url
                            ? "2px solid #ff3135"
                            : "2px solid transparent",
                        opacity: activeMedia.url === media.url ? 1 : 0.6,
                        transition: "all 0.2s",
                        flexShrink: 0,
                        position: "relative",
                      }}
                    >
                      <img
                        src={media.thumbnail}
                        alt={`media thumbnail ${idx}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {media.type === "video" && (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "rgba(0,0,0,0.5)",
                            borderRadius: "50%",
                            padding: "4px",
                            display: "flex",
                          }}
                        >
                          <Play size={12} fill="white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Backdrop>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </MessageAreaContainer>
  );
};

export default MessageArea;
